import { defineStore } from 'pinia'
import axios from 'axios'
import type {
  ArchivierungLoeschungAnonymisierung,
  BetroffenePersonen,
  Datenbeschreibung,
  Datenherkunft,
  Datenspeicherung,
  Datenverarbeitung,
  Datenweitergabe,
  PersonenFachklinikLaufzeit,
  Studienparameter,
  StudientypPlusEventuelleInformationenZumRegister,
  Study,
  TOMs,
  VerantwortlicheUndDSB,
} from '@/types/studyTypes'
import type { ValidationError, ChapterErrors, ValidationResult } from '@/validators/types'
import { validatePersonenFachklinikLaufzeit } from '@/validators/chapter0.validator'
import { validateStudienparameter } from '@/validators/chapter1.validator'
import { validateDatenbeschreibung } from '@/validators/chapter2.validator'
import { validateDatenherkunft } from '@/validators/chapter3.validator'
import { validateBetroffenePersonen } from '@/validators/chapter4.validator'
import { validateDatenverarbeitung } from '@/validators/chapter5.validator'
import { validateDatenspeicherung } from '@/validators/chapter6.validator'
import { validateDatenweitergabe } from '@/validators/chapter7.validator'
import { validateArchivierung } from '@/validators/chapter8.validator'
import { validateTOMs } from '@/validators/chapter10.validator'

import { reactive, computed, ref } from 'vue'
import { config } from '@/config'
import schema from '../data/study.json'
import schema_c0 from '../data/study-chapter0.json'
import schema_c1 from '../data/study-chapter1.json'
import schema_c2 from '../data/study-chapter2.json'
import schema_c3 from '../data/study-chapter3.json'
import schema_c4 from '../data/study-chapter4.json'
import schema_c5 from '../data/study-chapter5.json'
import schema_c6 from '../data/study-chapter6.json'
import schema_c7 from '../data/study-chapter7.json'
import schema_c8 from '../data/study-chapter8.json'
import schema_c9 from '../data/study-chapter9.json'
import schema_c10 from '../data/study-chapter10.json'

import testData from '@/test.json'

import Ajv2020 from 'ajv/dist/2020'
import addFormats from 'ajv-formats'

const ajv = new Ajv2020({ allErrors: true, useDefaults: true })
const schemas = [
  schema,
  schema_c0,
  schema_c1,
  schema_c2,
  schema_c3,
  schema_c4,
  schema_c5,
  schema_c6,
  schema_c7,
  schema_c8,
  schema_c9,
  schema_c10,
]
schemas.forEach((s) => ajv.addSchema(s))
addFormats(ajv)
const validateFn = ajv.compile(schema)

const PDF_ENDPOINT = `${config.API_BASE_URL}/study/generate-concept-pdf`
const DRAFT_ENDPOINT = `${config.API_BASE_URL}/study/generate-concept-pdf-draft`

const createBlankStudy = (): Study => ({
  personenFachklinikLaufzeit: {
    studientitel: '',
    ansprechpartner: undefined, // optional, starts undefined
    principalInvestigator: {
      academicTitle: '',
      firstName: '',
      lastName: '',
      email: '',
      klinik: [],
    },
    verantwortlicheFachabteilung: [],
    laufzeit: {},
    ohneEnddatum: false,
  },
  studienparameter: {
    eigeninitiiert: false,
    studientypContainer: {
      studienzentren: 'monozentrisch',
      studientyp: [],
      registerstudie: false,
      registerVerwendung: undefined,
      bestehendesRegister: undefined,
    } as StudientypPlusEventuelleInformationenZumRegister,
    einwilligung: {
      einwilligungLiegtVor: undefined,
      einwilligungsTyp: undefined,
      begruendungKeineEinwilligung: '',
    },
    ethikVotum: {
      ethikVotumStatus: undefined,
      ethikVotumNummer: '',
    },
    registrierung: [],
    registrierungSonstige: '',
    forschungszweck: '',
    idatRechtfertigung: false,
    idatRechtfertigungWeitere: '',
    studienaufbau: 'mdrIvdrMpdg',
  },
  datenbeschreibung: {
    artDerDaten: [],
    artDerGesundheitsDaten: undefined,
    artDerBiometrischenDaten: undefined,
    artDerDatenSonstige: '',
    bioProbenNachErfuellung: [],
    bioProbenNachErfuellungSonstiges: '',
  },
  datenherkunft: {
    einwilligungenWerdenEingeholt: undefined,
    datenQuellen: [],
    datenQuelleExtern: [],
    datenQuelleExternSonstiges: '',
    datenWeitererKliniken: undefined,
    datenQuelleSonstiges: '',
    datenQuelleEigeneRechercheDokumente: [],
    datenQuelleEigeneRechercheDokumenteSonstige: '',
    datenQuelleEigeneRechercheIdatBenoetigt: [],
    quellSysteme: [],
    quellSystemeSonstige: '',
    externeDaten: [],
    verwendungszweckVerknuepfungExtern: undefined,
    verwendeteGeraete: {
      geraete: [],
      mobilgeraeteHerkunft: [],
      verwendeteGeraeteSonstiges: undefined,
    },
  },
  betroffenePersonen: {
    personenGruppen: '',
    personenGruppenTyp: [],
    erwarteteFaelleKlinik: undefined,
    erwarteteFaelleGesamt: undefined,
    rechteEingeschraenkt: false,
    rechteEingeschraenktGrund: '',
  },
  datenverarbeitung: {
    weiterverarbeitung: [],
    verarbeitungsMethodenUndProgramme: '',
    verarbeitungMitKi: undefined,
    diga: undefined,
    bildgebendeVerfahren: undefined,
    publikationPersonenbezogenerDaten: false,
    publikationPersonenbezogenerDatenBeschreibung: '',
    datenfluss: '',
  },
  datenspeicherung: {
    enthaeltNichtPseudonymisierteDaten: false,
    verwendeteIdentifizierendeDaten: '',
    pseudonymisierung: [],
    pseudonymisierungPartner: [],
    pseudonymisierungSonstige: '',
    speicherFormat: [],
    speicherFormatDigitalMitPasswort: false,
    speicherungWaehrendErhebung: [],
    speicherungWaehrendErhebungSonstige: '',
  },
  datenweitergabe: {
    datenUebertragung: [],
    pseudonymlisteImHaus: undefined,
    pseudonymListeAusserHausBegruendungUndEmpfaenger: '',
    besonderheiten: '',
  },
  archivierungLoeschungAnonymisierung: {
    aalPersonenbezogeneDaten: undefined,
    aalPersonenbezogeneDatenDiz: '',
    aalPersonenbezogeneDatenSonstige: '',
    loeschfrist: undefined,
    loeschfristSonstige: '',
  },
  verantwortlicheUndDsb: {
    abweichungen: '',
    beteiligteInstitute: [],
  },
  TOMs: {
    benoetigteBerechtigungen: [],
    genutztePrimaersysteme: '',
    massnahmenBeiDerAuswertung: [],
    massnahmenBeiDerAuswertungWeitere: '',
    raeumlicheMassnahmen: [],
    raeumlicheMassnahmenExtern: [],
    raeumlicheMassnahmenExternWeitere: '',
    technischeMassnahmen: [],
    weitereTechnischeMassnahmen: '',
    organisatorischeMassnahmen: [],
    weitereOrganisatorischeMassnahmen: '',
  },
})

export const useStudyStore = defineStore('study', () => {
  const errors = reactive<ChapterErrors>({})
  const study: Study = reactive<Study>(createBlankStudy())
  // Which chapters the user has actually made an edit in. Drives the
  // "untouched" vs. "valid/invalid" state of the sidebar indicators.
  const touchedChapters = reactive<Partial<Record<keyof Study, boolean>>>({})
  // Bumped on every reset so views can force a remount and drop local UI
  // state (e.g. switches) that isn't itself derived from `study`.
  const resetVersion = ref(0)

  const resetStudy = () => {
    Object.assign(study, createBlankStudy())
    Object.keys(errors).forEach((key) => delete errors[key])
    Object.keys(touchedChapters).forEach((key) => delete touchedChapters[key as keyof Study])
    resetVersion.value++
  }

  // Runs every chapter's custom validator and refreshes `errors`. Cheap
  // synchronous checks over in-memory data, so it's safe to call on every
  // edit (see updateChapter) - unlike validateAll(), it doesn't touch the
  // AJV schema check or log anything, since it also runs silently on every
  // keystroke-adjacent change, not just on submit.
  function runValidators() {
    errors.personenFachklinikLaufzeit = validatePersonenFachklinikLaufzeit(
      study.personenFachklinikLaufzeit,
    )
    errors.studienparameter = validateStudienparameter(study.studienparameter)
    errors.datenbeschreibung = validateDatenbeschreibung(study.datenbeschreibung)
    errors.datenherkunft = validateDatenherkunft(study.datenherkunft)
    errors.betroffenePersonen = validateBetroffenePersonen(study.betroffenePersonen)
    errors.datenverarbeitung = validateDatenverarbeitung(study.datenverarbeitung)
    errors.datenspeicherung = validateDatenspeicherung(study.datenspeicherung)
    errors.datenweitergabe = validateDatenweitergabe(study.datenweitergabe)
    errors.archivierungLoeschungAnonymisierung = validateArchivierung(
      study.archivierungLoeschungAnonymisierung,
    )
    errors.TOMs = validateTOMs(study.TOMs)
  }

  // --- Actions ---
  // update a whole chapter
  const updateChapter = <K extends keyof Study>(chapter: K, value: Study[K]) => {
    study[chapter] = value
    touchedChapters[chapter] = true
    // A change in one chapter can affect another chapter's validity (e.g.
    // Chapter 7 reads defaults from Chapter 6), so re-check everything
    // rather than just the chapter that changed.
    runValidators()
  }

  // Various inputs (date pickers cleared, number inputs cleared, etc.) can
  // leave an explicit `null` on an optional field instead of just omitting
  // it. `null` isn't `undefined`, so it survives JSON.stringify into the
  // POST body, and it fails AJV's type/format checks even though the field
  // itself isn't required. Strip those out right before validation/submit
  // so an untouched-or-cleared optional field is genuinely absent.
  function stripNulls(value: unknown): void {
    if (Array.isArray(value)) {
      value.forEach(stripNulls)
      return
    }
    if (value === null || typeof value !== 'object') return
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === null) {
        delete (value as Record<string, unknown>)[key]
      } else {
        stripNulls(v)
      }
    }
  }

  const validateAll = (): ValidationResult => {
    stripNulls(study)
    const ajvValid = validateFn(study)
    runValidators()

    const hasCustomErrors = Object.values(errors).some((e) => e && e.length > 0)
    if (hasCustomErrors) {
      console.group('Validation errors')
      Object.entries(errors).forEach(([chapter, errs]) => {
        if (errs && errs.length > 0) {
          console.group(`📋 ${chapter}`)
          errs.forEach((e) => console.warn(`  ❌ ${e.field}: ${e.message}`))
          console.groupEnd()
        }
      })
      console.groupEnd()
      return { valid: false, reason: 'validation', errors }
    }

    if (!ajvValid) {
      console.warn('AJV schema errors:', validateFn.errors)
      return { valid: false, reason: 'schema', errors: validateFn.errors }
    }

    return { valid: true }
  }

  // Helper: get errors for a specific field path in a chapter
  const getFieldError = (chapter: keyof Study, field: string): string | undefined => {
    return errors[chapter]?.find((e) => e.field === field)?.message
  }

  // Helper: sidebar validation indicator state for a chapter
  const getChapterStatus = (chapter: keyof Study): 'untouched' | 'valid' | 'invalid' => {
    if (!touchedChapters[chapter]) return 'untouched'
    const chapterErrors = errors[chapter]
    return chapterErrors && chapterErrors.length > 0 ? 'invalid' : 'valid'
  }

  function triggerPdfDownload(data: Blob, filename: string) {
    const blob = new Blob([data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  async function handlePdfResponse(
    response: any,
    filename: string,
  ): Promise<{ success: boolean; reason?: string; errors?: any }> {
    const contentType = response.headers['content-type']
    if (contentType === 'application/pdf') {
      triggerPdfDownload(response.data, filename)
      return { success: true }
    }
    const text = await new Response(response.data).text()
    const errorJson = JSON.parse(text)
    return { success: false, errors: errorJson.errors }
  }

  async function submitStudy(
    draft: boolean,
  ): Promise<{ success: boolean; reason?: string; errors?: any }> {
    let targetUrl = DRAFT_ENDPOINT

    if (!draft) {
      targetUrl = PDF_ENDPOINT
      const result = validateAll()
      if (!result.valid) return { success: false, reason: result.reason, errors: result.errors }
    } else {
      stripNulls(study)
    }

    try {
      const response = await axios.post(targetUrl, study, {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      })
      return handlePdfResponse(
        response,
        `${study.personenFachklinikLaufzeit.studientitel || 'study'}.pdf`,
      )
    } catch (err: any) {
      console.error('Submission failed:', err)
      if (err.response?.status === 429) {
        return { success: false, reason: 'rateLimited', errors: err.response?.data || err.message }
      }
      return { success: false, errors: err.response?.data || err.message }
    }
  }

  async function submitTestdata(): Promise<{ success: boolean; reason?: string; errors?: any }> {
    try {
      const response = await axios.post(PDF_ENDPOINT, testData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      })
      return handlePdfResponse(
        response,
        `${study.personenFachklinikLaufzeit.studientitel || 'study'}.pdf`,
      )
    } catch (err: any) {
      console.error('Submission failed:', err)
      if (err.response?.status === 429) {
        return { success: false, reason: 'rateLimited', errors: err.response?.data || err.message }
      }
      return { success: false, errors: err.response?.data || err.message }
    }
  }

  return {
    study,
    errors,
    touchedChapters,
    resetVersion,
    updateChapter,
    validateAll,
    getFieldError,
    getChapterStatus,
    submitStudy,
    submitTestdata,
    resetStudy,
  }
})
